import { motion } from "framer-motion";
import { Overline, Reveal, MaskLine, ArrowLink, scrollToId } from "./shared";
import { useLang } from "@/i18n";

const productsMeta = [
  { id: "black-mustard", num: "01", img: "/assets/label-black-mustard.webp", accent: "#1A1D1A" },
  { id: "yellow-mustard", num: "02", img: "/assets/label-yellow-mustard.webp", accent: "#D49A36" },
  { id: "coconut", num: "03", img: "/assets/label-coconut.webp", accent: "#4A5240" },
  { id: "almond", num: "04", img: "/assets/label-almond.webp", accent: "#B85C38" },
];

export default function Products() {
  const { t } = useLang();
  const items = t("products.items");

  return (
    <section id="oils" className="bg-sand/60 py-24 lg:py-32" data-testid="products-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Overline>{t("products.overline")}</Overline>
            <h2 className="mt-4 font-serif text-4xl font-medium leading-tight tracking-tight sm:text-5xl lg:text-6xl">
              <MaskLine>{t("products.title1")}</MaskLine>
              <MaskLine delay={0.12}>
                <span className="italic text-olive">{t("products.title2")}</span>
              </MaskLine>
            </h2>
          </div>
          <Reveal delay={0.2}>
            <p className="max-w-xs text-sm font-light leading-relaxed text-moss">
              {t("products.side")}
            </p>
          </Reveal>
        </div>

        <div className="mt-16 space-y-20 lg:mt-24 lg:space-y-28">
          {productsMeta.map((m, i) => {
            const p = items[i];
            return (
              <Reveal key={m.id} className="group grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
                <div className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                  <div className="relative overflow-hidden border border-ink/10 bg-cream" data-testid={`product-image-${m.id}`}>
                    <motion.img
                      src={m.img}
                      alt={`${p.name} product label`}
                      className="aspect-[16/9] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                      loading="lazy"
                    />
                    <span
                      className="absolute left-4 top-4 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-cream"
                      style={{ backgroundColor: m.accent }}
                    >
                      {p.press}
                    </span>
                  </div>
                </div>
                <div className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-1 lg:pr-8" : "lg:pl-8"}`}>
                  <span className="font-serif text-6xl font-light leading-none text-outline">{m.num}</span>
                  <h3 className="mt-4 font-serif text-3xl font-semibold tracking-tight sm:text-4xl" data-testid={`product-name-${m.id}`}>
                    {p.name}
                  </h3>
                  <p className="mt-3 max-w-md text-base font-light leading-relaxed text-moss">{p.notes}</p>
                  <div className="mt-6 flex flex-wrap gap-x-8 gap-y-2 border-t border-ink/10 pt-5 text-xs uppercase tracking-[0.18em] text-moss">
                    <span>{p.size}</span>
                    <span>{p.uses}</span>
                  </div>
                  <div className="mt-7">
                    <ArrowLink onClick={() => scrollToId("#enquiry")} testId={`product-enquire-${m.id}`}>
                      {t("products.enquire")}
                    </ArrowLink>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
